import { useCallback, useId, type FC } from "react";
import { Input } from "@/components/podkit/forms/Input";
import { InputField } from "@/components/podkit/forms/InputField";
import type { WorkflowEditFormLimitsData } from "@/components/automations/automation-edit-form-data";
import { DEFAULT_WORKFLOW_LIMITS } from "@/utils/automations";

type WorkflowEditActionProps = {
    limits?: WorkflowEditFormLimitsData;
    onLimitsChange: (limits: WorkflowEditFormLimitsData | undefined) => void;
};

export const WorkflowEditAction: FC<WorkflowEditActionProps> = ({ limits, onLimitsChange }) => {
    const maxParallelId = useId();
    const maxTotalId = useId();

    const handleMaxParallelChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            const numValue = value === "" ? undefined : parseInt(value, 10);
            onLimitsChange({
                ...limits,
                maxParallel: numValue,
            });
        },
        [limits, onLimitsChange],
    );

    const handleMaxTotalChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            const numValue = value === "" ? undefined : parseInt(value, 10);
            onLimitsChange({
                ...limits,
                maxTotal: numValue,
            });
        },
        [limits, onLimitsChange],
    );

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-4">
                <div className="flex flex-wrap items-center gap-4">
                    <InputField
                        label="Max concurrent actions"
                        hint="The number of actions that can run in parallel per execution"
                        id={maxParallelId}
                    >
                        <Input
                            id={maxParallelId}
                            type="number"
                            min={1}
                            max={25}
                            value={limits?.maxParallel ?? ""}
                            onChange={handleMaxParallelChange}
                            placeholder={String(DEFAULT_WORKFLOW_LIMITS.maxParallel)}
                            className="max-w-[300px] bg-transparent"
                            data-testid="max-parallel-input"
                        />
                    </InputField>
                    <InputField
                        label="Max total actions"
                        hint="The total number of actions that can be created by a single execution"
                        id={maxTotalId}
                    >
                        <Input
                            id={maxTotalId}
                            type="number"
                            min={1}
                            max={100}
                            value={limits?.maxTotal ?? ""}
                            onChange={handleMaxTotalChange}
                            placeholder={String(DEFAULT_WORKFLOW_LIMITS.maxTotal)}
                            className="max-w-[300px] bg-transparent"
                            data-testid="max-total-input"
                        />
                    </InputField>
                </div>
                <div className="text-sm text-content-secondary">
                    Note: These limits are currently restricted during beta to ensure system stability. Higher limits
                    will be available after the beta period.
                </div>
            </div>
        </div>
    );
};
